import { useContext, useEffect, useState, useCallback, useRef } from "react";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { Skeleton } from "primereact/skeleton";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import CourseService from "../../../../services/CourseService";
import { useNavigate } from "react-router-dom";

const TotalCountsCard = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();
  const cacheRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  useEffect(() => {
    const courseAccess = authContext.getPermissions("courses");
    if (courseAccess.filter((f) => f.access.includes(1)).length === 0 || authContext.onGoingValidations) {
      navigate("/");
    }
    setShowAdd(courseAccess.filter((f) => f.access.includes(2)).length > 0);
  }, [navigate, authContext]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 576);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

const loadDashboard = useCallback(async (signal) => {
    if (cacheRef.current) {
      setCardData(cacheRef.current);
      loadingTimeoutRef.current = setTimeout(() => {
        setLoading(false);
      }, 300);
      return;
    }

    setLoading(true);
    try {
      const response = await CourseService.GetDashboard(
        authContext,
        signal 
      );

      if (signal?.aborted) return;

      if (response.isSuccess) {
        const data = [
          { title: "Total Courses", value: response.data.totalCourses || 0 },
          { title: "Completed Courses", value: response.data.completedCourses || 0 },
          { title: "Active Courses", value: response.data.activeCourses || 0 },
          { title: "Total Learners", value: response.data.totalUsers || 0 },
        ];

        cacheRef.current = data;
        setCardData(data);
        if (!signal?.aborted) {
          loadingTimeoutRef.current = setTimeout(() => {
            setLoading(false);
          }, 300);
        }
      } else {
        AlertService.error(response.errorMessage || "Failed to fetch dashboard data");
        if (!signal?.aborted) setLoading(false);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      AlertService.error("Failed to fetch dashboard data");
      console.error(err);
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);
useEffect(() => {
    const controller = new AbortController(); 
    loadDashboard(controller.signal); 
    return () => {
      controller.abort();
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [loadDashboard]);
  return (
    <div
      className="pb-3"
     style={{
  width: "100%",
  height: isMobile ? "auto" : "280px", 
  transition: "height 0.3s ease",
}}
    >
      <div className="row g-3 h-100 m-0" style={{ alignContent: "center" }}>
        {(loading ? Array.from({ length: showAdd ? 4 : 3 }) : cardData.filter((stat) => showAdd || stat.title !== "Total Learners")).map(
          (stat, idx) => {
            const isActiveCourses = stat?.title === "Active Courses" || (loading && idx === 2 && !showAdd);
            return (
              <div
                key={idx}
                className={`col-12 ${isMobile ? "" : showAdd || !isActiveCourses ? "col-sm-6" : "col-sm-6 offset-sm-3"}`}
              >
                {loading ? (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center h-100 p-1 charts"
                    style={{
                      minHeight: "132px",
                      borderRadius: "10px",
                    }}
                  >
                    <Skeleton width="60%" height="20px" className="mb-2" />
                    <Skeleton width="40%" height="30px" />
                  </div>
                ) : (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center h-100 charts p-1"
                    style={{
                      minHeight: "132px",
                      borderRadius: "10px",
                    }}
                  >
                    <div
                      className="mb-2"
                      style={{
                        textAlign: "center",
                        color: isDark ? "#dddddd" : "#003366",
                      }}
                    >
                      {stat.title}
                    </div>
                    <div
                      style={{
                        color: isDark ? "#dddddd" : "#003366",
                        fontSize: "1.875rem",
                        fontWeight: "700",
                        textAlign: "center",
                      }}
                    >
                      {stat.value}
                    </div>
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default TotalCountsCard;